import { api } from '../../../js/api/core/request';
import { filenameFromContentDisposition } from './downloadQuestionnairePdf';

/** Hand `blob` to the browser as a file named `filename`. */
const saveBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.setAttribute('download', filename);
  document.body.appendChild(link);
  link.click();
  link.parentNode?.removeChild(link);
  window.URL.revokeObjectURL(url);
};

/**
 * Save a connection's presentation as a PDF
 * (`GET /api/connections/connected-facility/{id}/presentation/pdf/`). Generated on
 * every request from the same payload the Presentation Info tab shows — never a
 * stored copy. The server names the file.
 */
export async function downloadPresentationPdf(connectionId: string): Promise<string> {
  const response = await api.get<Blob>(
    `/api/connections/connected-facility/${connectionId}/presentation/pdf/`,
    { responseType: 'blob' }
  );
  const filename =
    filenameFromContentDisposition(response.headers['content-disposition'] as string) ??
    'Clinician Presentation.pdf';
  saveBlob(response.data, filename);
  return filename;
}

export type PresentationPackageResult = { filename: string; cvIncluded: boolean };

/**
 * Save the presentation package — the presentation PDF plus the clinician's CV — as
 * one zip (`GET .../presentation/package/`). The server says whether a CV went in.
 */
export async function downloadPresentationPackage(
  connectionId: string
): Promise<PresentationPackageResult> {
  const response = await api.get<Blob>(
    `/api/connections/connected-facility/${connectionId}/presentation/package/`,
    { responseType: 'blob' }
  );
  const filename =
    filenameFromContentDisposition(response.headers['content-disposition'] as string) ??
    'Clinician Presentation Package.zip';
  saveBlob(response.data, filename);
  return { filename, cvIncluded: response.headers['x-presentation-cv'] === '1' };
}
